import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "./Chatbot.css";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { secureStorage } from "./utils/secureStorage";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const makeAuthorizedRequest = async (axiosConfig) => {
  try {
    const token = secureStorage.getItem("token");
    const config = {
      ...axiosConfig,
      url: `${API_BASE_URL}${axiosConfig.url}`,
      headers: {
        ...axiosConfig.headers,
        Authorization: `Bearer ${token}`,
      },
    };
    return await axios(config);
  } catch (err) {
    if (err.response && err.response.status === 401) {
      const refreshed = await attemptTokenRefresh();
      if (refreshed) {
        const token = secureStorage.getItem("token");
        const retryConfig = {
          ...axiosConfig,
          url: `${API_BASE_URL}${axiosConfig.url}`,
          headers: {
            ...axiosConfig.headers,
            Authorization: `Bearer ${token}`,
          },
        };
        return await axios(retryConfig);
      }
    }
    throw err;
  }
};

const attemptTokenRefresh = async () => {
  const refreshToken = secureStorage.getItem("refreshToken");
  if (!refreshToken) return false;

  try {
    const response = await axios.post(`${API_BASE_URL}/refresh`, {
      refresh_token: refreshToken,
    });
    secureStorage.setItem("token", response.data.access_token);
    return true;
  } catch (error) {
    console.error("Refresh token failed:", error);
    secureStorage.removeItem("token");
    secureStorage.removeItem("refreshToken");
    window.location.reload();
    return false;
  }
};

const Chatbot = ({
  activeChat,
  setActiveChat,
  onNewChatCreated,
  resumeAvailable,
  onResumeChat,
  chatResetSignal,
}) => {
  const [question, setQuestion] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const chatContainerRef = useRef(null);

  const formatResponse = (text) => {
    return text;
  };

  useEffect(() => {
    const fetchChatHistory = async () => {
      if (!activeChat) return;
      try {
        const response = await makeAuthorizedRequest({
          method: "GET",
          url: `/api/chat/messages/${activeChat}`,
        });

        const messages = response.data.map((msg) => ({
          sender: msg.sender === "user" ? "You" : "Coby",
          text: msg.sender === "bot" ? formatResponse(msg.content) : msg.content,
        }));
        setChatHistory(messages);
      } catch (error) {
        console.error("Error fetching chat history:", error);
      }
    };

    fetchChatHistory();
  }, [activeChat]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory]);

  useEffect(() => {
    if (!activeChat) {
      console.log("🧹 Resetting chat state from signal");
      setChatHistory([]);
      setQuestion("");
      setIsTyping(false);
    }
  }, [chatResetSignal, activeChat]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;

    const newChatHistory = [...chatHistory, { sender: "You", text: question }];
    setChatHistory(newChatHistory);
    setQuestion("");
    setIsTyping(true);

    try {
      let currentSessionId = activeChat;
      let isNewSession = false;

      if (!currentSessionId) {
        const sessionResponse = await makeAuthorizedRequest({
          method: "POST",
          url: "/api/chat/sessions",
          data: { title: question.substring(0, 30) || "New Chat" },
        });

        currentSessionId = sessionResponse.data.id;
        setActiveChat(currentSessionId);
        onNewChatCreated?.();
        isNewSession = true;
      }

      const askResponse = await makeAuthorizedRequest({
        method: "POST",
        url: "/ask/",
        data: { session_id: currentSessionId, question },
      });

      if (isNewSession) {
        await makeAuthorizedRequest({
          method: "PUT",
          url: `/api/chat/sessions/${currentSessionId}`,
          data: { title: question.substring(0, 60) },
        });
      }

      setIsTyping(false);

      if (askResponse.status === 200) {
        const updatedHistory = [
          ...newChatHistory,
          { sender: "Coby", text: formatResponse(askResponse.data.answer) },
        ];
        setChatHistory(updatedHistory);
      } else {
        console.error("Error:", askResponse.data);
      }
    } catch (error) {
      console.error("Error fetching response:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });
      setIsTyping(false);
    }
  };

  const starterMessages = [
    "💬 Ready when you are",
    "🤔 What would you like to know today?",
    "🧠 Need help with something?",
    "❓ Ask me anything!",
    "🚀 Where should we begin?",
    "👋 I’m here if you need me.",
  ];

  const [starterMessage] = useState(() => {
    return starterMessages[Math.floor(Math.random() * starterMessages.length)];
  });

  return (
    <div className="chatbot-container">
      <div className="chatbot-wrapper">
        <div ref={chatContainerRef} className="chatbot-messages">
          {(!activeChat && chatHistory.length === 0) ? (
            <div className="starter-screen">
              <h1 className="starter-heading">{starterMessage}</h1>
              <p className="starter-subtext">Ask me anything, or start a new chat.</p>
              {resumeAvailable && (
                <button className="resume-button" onClick={onResumeChat}>
                  Resume Last Chat
                </button>
              )}
            </div>
          ) : (
            <>
              {chatHistory.map((msg, index) => (
                <div
                  key={index}
                  className={`message-bubble ${msg.sender === "You" ? "user" : "bot"}`}
                >
                  <strong className="message-sender">{msg.sender}</strong>
                  <div className="message-text">
                    {msg.sender === "You" ? (
                      msg.text
                    ) : (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                    )}
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="typing-indicator">
                  <strong className="message-sender">Coby</strong>
                  <p style={{ fontStyle: "italic" }}>Typing...</p>
                </div>
              )}
            </>
          )}
        </div>

        {(!resumeAvailable || activeChat) && (
          <form onSubmit={handleSubmit} className="input-container centered-input">
            <input
              type="text"
              placeholder="Message Coby..."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              className="chatbot-input big-input"
            />
            <button type="submit" className="send-button">➤</button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Chatbot;


// import React, { useEffect, useRef, useState } from "react";
// import axios from "axios";
// import "./Chatbot.css";
// import ReactMarkdown from "react-markdown";
// import remarkGfm from "remark-gfm";

// const makeAuthorizedRequest = async (axiosConfig) => {
//   try {
//     const token = localStorage.getItem("token");
//     const config = {
//       ...axiosConfig,
//       headers: {
//         ...axiosConfig.headers,
//         Authorization: `Bearer ${token}`,
//       },
//     };
//     return await axios(config);
//   } catch (err) {
//     if (err.response && err.response.status === 401) {
//       const refreshed = await attemptTokenRefresh();
//       if (refreshed) {
//         const token = localStorage.getItem("token");
//         const retryConfig = {
//           ...axiosConfig,
//           headers: {
//             ...axiosConfig.headers,
//             Authorization: `Bearer ${token}`,
//           },
//         };
//         return await axios(retryConfig);
//       }
//     }
//     throw err;
//   }
// };

// const attemptTokenRefresh = async () => {
//   const refreshToken = localStorage.getItem("refreshToken");
//   if (!refreshToken) return false;

//   try {
//     const response = await axios.post("/refresh", { refresh_token: refreshToken });
//     localStorage.setItem("token", response.data.access_token);
//     return true;
//   } catch (error) {
//     console.error("Refresh token failed:", error);
//     localStorage.removeItem("token");
//     localStorage.removeItem("refreshToken");
//     window.location.reload();
//     return false;
//   }
// };

// const Chatbot = ({
//   activeChat,
//   setActiveChat,
//   onNewChatCreated,
//   resumeAvailable,
//   onResumeChat,
//   chatResetSignal,
// }) => {
//   const [question, setQuestion] = useState("");
//   const [chatHistory, setChatHistory] = useState([]);
//   const [isTyping, setIsTyping] = useState(false);
//   const chatContainerRef = useRef(null);

//   const formatResponse = (text) => {
//     return text; // ✅ return raw markdown string
//   };


//   const convertMarkdownTableToHTML = (markdown) => {
//     const rows = markdown.trim().split("\n");
//     const headerRow = rows[0];
//     const dataRows = rows.slice(2);
//     const headers = headerRow
//       .split("|")
//       .map((header) => header.trim())
//       .filter((header) => header !== "");

//     let tableHTML = `<table><tr>${headers.map((header) => `<th>${header}</th>`).join("")}</tr>`;
//     dataRows.forEach((row) => {
//       const columns = row
//         .split("|")
//         .map((col) => col.trim())
//         .filter((col) => col !== "");
//       tableHTML += `<tr>${columns.map((col) => `<td>${col}</td>`).join("")}</tr>`;
//     });
//     tableHTML += "</table>";
//     return tableHTML;
//   };

//   useEffect(() => {
//     const fetchChatHistory = async () => {
//       if (!activeChat) return;
//       try {
//         const response = await makeAuthorizedRequest({
//           method: "GET",
//           url: `/api/chat/messages/${activeChat}`,
//         });

//         const messages = response.data.map((msg) => ({
//           sender: msg.sender === "user" ? "You" : "Coby",
//           text: msg.sender === "bot" ? formatResponse(msg.content) : msg.content,
//         }));
//         setChatHistory(messages);
//       } catch (error) {
//         console.error("Error fetching chat history:", error);
//       }
//     };

//     fetchChatHistory();
//   }, [activeChat]);

//   useEffect(() => {
//     if (chatContainerRef.current) {
//       chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
//     }
//   }, [chatHistory]);

//   useEffect(() => {
//     if (!activeChat) {
//       console.log("🧹 Resetting chat state from signal");
//       setChatHistory([]);
//       setQuestion("");
//       setIsTyping(false);
//     }
//   }, [chatResetSignal, activeChat]);

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!question.trim()) return;

//     const newChatHistory = [...chatHistory, { sender: "You", text: question }];
//     setChatHistory(newChatHistory);
//     setQuestion("");
//     setIsTyping(true);

//     try {
//       let currentSessionId = activeChat;
//       let isNewSession = false;

//       if (!currentSessionId) {
//         const sessionResponse = await makeAuthorizedRequest({
//           method: "POST",
//           url: "/api/chat/sessions",
//           data: { title: question.substring(0, 30) || "New Chat" },
//         });

//         currentSessionId = sessionResponse.data.id;
//         setActiveChat(currentSessionId);
//         onNewChatCreated?.();
//         isNewSession = true;
//       }

//       const askResponse = await makeAuthorizedRequest({
//         method: "POST",
//         url: "/ask/",
//         data: { session_id: currentSessionId, question },
//       });

//       if (isNewSession) {
//         await makeAuthorizedRequest({
//           method: "PUT",
//           url: `/api/chat/sessions/${currentSessionId}`,
//           data: { title: question.substring(0, 60) },
//         });
//       }

//       setIsTyping(false);

//       if (askResponse.status === 200) {
//         const updatedHistory = [
//           ...newChatHistory,
//           { sender: "Coby", text: formatResponse(askResponse.data.answer) },
//         ];
//         setChatHistory(updatedHistory);
//       } else {
//         console.error("Error:", askResponse.data);
//       }
//     } catch (error) {
//       console.error("Error fetching response:", {
//         message: error.message,
//         status: error.response?.status,
//         data: error.response?.data,
//       });
//       setIsTyping(false);
//     }
//   };

//   const starterMessages = [
//     "💬 Ready when you are",
//     "🤔 What would you like to know today?",
//     "🧠 Need help with something?",
//     "❓ Ask me anything!",
//     "🚀 Where should we begin?",
//     "👋 I’m here if you need me.",
//   ];

//   const [starterMessage] = useState(() => {
//     return starterMessages[Math.floor(Math.random() * starterMessages.length)];
//   });


//   return (
//     <div className="chatbot-container">
//       <div className="chatbot-wrapper">
//         <div ref={chatContainerRef} className="chatbot-messages">
//           {(!activeChat && chatHistory.length === 0) ? (
//             <div className="starter-screen">
//               <h1 className="starter-heading">{starterMessage}</h1>
//               <p className="starter-subtext">Ask me anything, or start a new chat.</p>
//               {resumeAvailable && (
//                 <button className="resume-button" onClick={onResumeChat}>
//                   Resume Last Chat
//                 </button>
//               )}
//             </div>
//           ) : (
//             <>
//               {chatHistory.map((msg, index) => (
//                 <div
//                   key={index}
//                   className={`message-bubble ${msg.sender === "You" ? "user" : "bot"}`}
//                 >
//                   <strong className="message-sender">{msg.sender}</strong>
//                   <div className="message-text">
//                     {msg.sender === "You" ? (
//                       msg.text
//                     ) : (
//                       // <ReactMarkdown>{msg.text}</ReactMarkdown>
//                       <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
//                     )}
//                   </div>
//                 </div>
//               ))}
//               {isTyping && (
//                 <div className="typing-indicator">
//                   <strong className="message-sender">Coby</strong>
//                   <p style={{ fontStyle: "italic" }}>Typing...</p>
//                 </div>
//               )}
//             </>
//           )}
//         </div>

//         {(!resumeAvailable || activeChat) && (
//           <form onSubmit={handleSubmit} className="input-container centered-input">
//             <input
//               type="text"
//               placeholder="Message Coby..."
//               value={question}
//               onChange={(e) => setQuestion(e.target.value)}
//               className="chatbot-input big-input"
//             />
//             <button type="submit" className="send-button">➤</button>
//           </form>
//         )}
//       </div>
//     </div>
//   );



// }

// export default Chatbot;

